import React from 'react';
import PropTypes from 'prop-types';
import { Box, Pagination } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDynamicBreakpoints } from '../utils/breakpointUtils';
import { useSelector } from 'react-redux';

const GlobalPagination = ({
    count,
    page,
    onChange,
    showFirstButton = false,
    showLastButton = false,
    siblingCount,
    boundaryCount = 1,
    ...otherProps
}) => {
    const theme = useTheme();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { matchDownSM, matchDownMD } = useDynamicBreakpoints(drawerOpen);

    const size = matchDownSM ? "small" : (matchDownMD ? "small" : "medium");

    // Calculate dynamic siblingCount - more responsive
    const calculatedSiblingCount = siblingCount !== undefined
        ? siblingCount
        : matchDownSM ? 0 : (matchDownMD ? 0 : 0);

    // Adjust boundaryCount based on screen size
    const adjustedBoundaryCount = matchDownSM ? 1 : (matchDownMD ? 1 : boundaryCount);

    // Consistent styles for all pagination instances
    const getBaseStyles = () => ({
        '& .MuiPaginationItem-root': {
            width: matchDownSM ? 26 : (matchDownMD ? 30 : 32),
            height: matchDownSM ? 26 : (matchDownMD ? 30 : 32),
            borderRadius: '8px',
            fontSize: matchDownSM ? '0.7rem' : (matchDownMD ? '0.75rem' : '0.85rem'),
            fontWeight: 400,
            border: 1,
            borderColor: theme.palette.divider,
            bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.04)',
            color: theme.palette.text.secondary,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            margin: matchDownSM ? '0 1px' : (matchDownMD ? '0 2px' : '0 3px'),
            minWidth: 'unset', // Prevent MUI minimum width constraints
            '&:hover': {
                bgcolor: theme.palette.action.hover,
                borderColor: theme.palette.primary.main,
                transform: 'scale(1.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 2
            }
        },
        '& .MuiPaginationItem-page.Mui-selected': {
            bgcolor: theme.palette.mode === 'dark'
                ? theme.palette.primary.light
                : theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            color: theme.palette.mode === 'dark'
                ? theme.palette.primary.contrastText
                : theme.palette.common.white,
            fontWeight: 700,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
            transform: 'scale(1.05)',
            zIndex: 3,
            '&:hover': {
                bgcolor: theme.palette.mode === 'dark'
                    ? theme.palette.primary.main
                    : theme.palette.primary.dark,
                borderColor: theme.palette.primary.dark,
                transform: 'scale(1.15)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.3)'
            }
        },
        '& .MuiPaginationItem-previousNext': {
            width: matchDownSM ? 28 : (matchDownMD ? 32 : 36),
            height: matchDownSM ? 28 : (matchDownMD ? 32 : 36),
            bgcolor: 'primary.main',
            borderColor: 'primary.main',
            color: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            minWidth: 'unset', // Prevent MUI minimum width constraints
            '&:hover': {
                bgcolor: 'primary.dark',
                color: 'white',
                transform: 'scale(1.1)',
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25)',
                zIndex: 2
            },
            '&.Mui-disabled': {
                opacity: 0.5,
                bgcolor: 'primary.main',
                borderColor: 'primary.main',
                color: 'white',
                transform: 'none',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
            },
            '&:first-of-type': {
                marginRight: matchDownSM ? '8px' : (matchDownMD ? '10px' : '16px')
            },
            '&:last-of-type': {
                marginLeft: matchDownSM ? '8px' : (matchDownMD ? '10px' : '16px')
            }
        }
    });

    if (count <= 1) return null;

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mt: 2,
            pt: 2,
            width: '100%',
            flexShrink: 0,
            minHeight: matchDownSM ? 32 : (matchDownMD ? 36 : 40)
        }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                maxWidth: '100%',
            }}>
                <Pagination
                    count={count}
                    page={page}
                    onChange={onChange}
                    color="primary"
                    size={size}
                    showFirstButton={showFirstButton}
                    showLastButton={showLastButton}
                    siblingCount={calculatedSiblingCount}
                    boundaryCount={adjustedBoundaryCount}
                    sx={getBaseStyles()}
                    {...otherProps}
                />
            </Box>
        </Box>
    );
};

GlobalPagination.propTypes = {
    count: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    showFirstButton: PropTypes.bool,
    showLastButton: PropTypes.bool,
    siblingCount: PropTypes.number,
    boundaryCount: PropTypes.number
};

export default GlobalPagination;